import { Fragment, useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import { withTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { detect } from 'detect-browser'

import { withStyles } from 'beam/dist/styles'
import { Grid } from 'beam/dist/Grid'

import { dataWrapperPropType } from './MediaPlayer'
import { CONTACT_TYPE } from './mediaPlayer.constants'
import { PANEL_SIZES, canEvaluationComponentRender, canCalibrationComponentRender } from './helpers'

import TimelinePanel from '../TimelinePanel'
import ScreenCapture from '../ScreenCapture'
import DesktopPanel from '../DesktopPanel'
import Transcription from '../Transcription'
import Sentiment from '../Sentiment'
import { AdvSentiment } from '../AdvSentiment'
import SpeechHitsPanel from '../SpeechHitsPanel'
import Explore from '../Explore'
import MultiEvaluation from '../MultiEvaluation'
import SingleEvaluation from '../SingleEvaluationForm'
import TextPanel from '../TextPanel'
import Chat from '../Chat'
import AudioPanel from '../AudioPanel'
import ErrorBoundary from '../ErrorBoundary'
import { InteractionSummary } from '../InteractionSummary'
import { ChatMapPanel } from '../ChatMapPanel'
import { authenticatedUserShape, withAuthenticatedUser, PERMISSIONS } from '../../../Permissions'
import * as PAGE_PERMISSIONS from '../../../Permissions/pagePermissions.constants'
import { FEATURE_FLAGS, useFeatureFlagValue } from '../../../FeatureFlags'
import moment from 'moment'
import { SelectedChatBubbleContext } from '../ChatMapPanel/constants'
import { useOrganization } from '../../../Common/Organization/organization-context'
import { trackOpenedMediaPlayer } from '../../pendoUtils'

const styles = theme => ({
  panelRoot: {
    backgroundColor: theme.palette.grey[300],
  },
  exploreItem: {
    paddingRight: theme.spacing(2),
  },
  sentimentPanel: {
    flex: `0 0 ${theme.spacing(PANEL_SIZES.SENTIMENT)}px`,
    minHeight: 0,
  },
  transcriptionPanel: {
    flex: `1 1 ${theme.spacing(PANEL_SIZES.TRANSCRIPTION)}px`,
    minHeight: 0,
  },
  timelinePanel: {
    flex: `0 0 ${theme.spacing(PANEL_SIZES.TIMELINE)}px`,
    minHeight: 0,
  },
  audioPanel: {
    flex: `1 1 ${theme.spacing(PANEL_SIZES.AUDIO)}px`,
    maxWidth: `${theme.spacing(PANEL_SIZES.AUDIO)}px`,
    minHeight: 0,
  },
  speechPanel: {
    flex: `1 1 ${theme.spacing(PANEL_SIZES.SPEECH)}px`,
    minHeight: 0,
  },
  explorePanel: {
    height: '100%',
  },
  desktopPanel: {
    flex: `0 0 ${theme.spacing(PANEL_SIZES.DESKTOP)}px`, // keep until desktop uses svg and not position absolute
  },
  textPanel: {
    flex: '1 1 auto',
    minHeight: 0,
  },
  screenCapture: {
    flex: '1 1 auto',
    width: '100%',
    minHeight: 0,
  },
  halfHeight: {
    flex: '1 1 auto',
    height: '50%',
  },
  greedyScreenCapture: {
    height: '100%',
    width: '100%',
  },
  evalForm: {
    flex: `1 0 ${theme.spacing(49)}px`,
    height: '100%',
  },
  flexGrow: {
    flex: '1 1 auto',
  },
  analyticsContainer: {
    boxShadow: theme.shadows[4],
    flex: '1',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: 0,
    maxHeight: '100%',
  },
  transcriptionParent: {
    display: 'flex',
    flex: '1',
    minWidth: 0,
    flexDirection: 'column',
  },
  timelineAudioContainer: {
    boxShadow: theme.shadows[4],
    height: '100%',
    width: '164px',
  },
  hidden: {
    display: 'none',
  },
  chatContainer: {
    boxShadow: theme.shadows[4],
    flex: '1',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    minHeight: 0,
    maxHeight: '100%',
  },
  chatPanel: {
    flex: `1 1 ${theme.spacing(PANEL_SIZES.CHAT)}px`,
    minHeight: 0,
  },
  chatMapPanel: {
    width: '112px',
  },
})

const colWidth = colWidth => ({
  flexGrow: 0,
  maxWidth: colWidth,
  flexBasis: colWidth,
})

const browser = detect()
const isIE = browser.name === 'ie'

const MediaPlayerWorkspace = props => {
  const {
    classes,
    theme,
    authenticatedUser: { hasPermission, hasAnyPermission, hasAllPermissions, user },
    ccrid,
    media: {
      main: { contact, storageFile, licensedFeatures },
      transcription,
      desktop,
      speech,
      energy,
      chat,
    },
    isExpanded,
    isZoomed,
    isMediaPlaying,
    hasVideo,
    hasAudioData,
    hasVideoData,
    mediaTimeline: { videoStart, videoEnd, duration, audioDuration, audioStart, audioStorageFileOffset },
    mediaCurrentTime,
    mediaPlaybackRate,
    hasDx,
    timezoneOverride,
    videoRestoreDeclined,
    onSetTime,
    onScrubStart,
    onScrubEnd,
    onGetMediaCurrentTime,
    onOpenContact,
    onPopupsBlocked,
    onVideoHasErrors,
    videoUploadFailed,
    goalId,
    autoQAAIAnalysisHasData,
    autoQAAutoEvaluationHasData,
    autoQAReloadTimestamp,
    autoQAFormId,
    advSentimentTrendGraphHasData,
  } = props

  const {
    data: { contactType, isRootRecording, recordingType },
  } = contact

  const advSentimentEnabled = useFeatureFlagValue(FEATURE_FLAGS.ANALYTICS_ENABLE_ADVANCED_SENTIMENT)

  const { agentData } = useOrganization()

  const [selectedBubble, setSelectedBubble] = useState(null)

  const [arePopupsEnabled, setArePopupsEnabled] = useState(!isIE)

  const hasAutoQAPermission = hasAnyPermission(PAGE_PERMISSIONS.AUTO_QA_INTERACTION)

  const handlePopupError = useCallback(() => {
    setArePopupsEnabled(false)
    onPopupsBlocked(true)
  }, [])

  const [isScreenDocked, setIsScreenDocked] = useState(true)
  const [isEvalDocked, setIsEvalDocked] = useState(hasPermission(PERMISSIONS.VIEW_EVALUATIONS) || (!shouldHideTranscription && hasAutoQAPermission))

  useEffect(() => {
    if (contactType && recordingType) {
      trackOpenedMediaPlayer({
        contactType,
        recordingType,
      })
    }
  }, [contactType, recordingType])

  // Getting energy data doesn't guarantee we actually have meaningful data
  const [hasEnergyData, setHasEnergyData] = useState(null)
  useEffect(() => {
    if (!energy.loading) {
      if (!energy.data) {
        setHasEnergyData(false)
      } else {
        const {
          data: { channel_0, channel_1 },
        } = energy

        const hasSomeEnergy = (channel_0 && channel_0.some(x => x !== 0)) || (channel_1 && channel_1.some(x => x !== 0))

        setHasEnergyData(hasSomeEnergy)
      }
    }

    return function cleanup() {
      setHasEnergyData(null)
    }
  }, [energy, energy.loading, energy.data])

  // Getting transcription data doesn't guarantee we actually have any hits
  const [hasTranscriptionHits, setHasTranscriptionHits] = useState(null)
  useEffect(() => {
    if (!transcription.loading) {
      const hasHits = transcription.texts.some(text => text.hits.length > 0)

      setHasTranscriptionHits(hasHits)
    }

    return function cleanup() {
      setHasTranscriptionHits(null)
    }
  }, [transcription])

  const hasDesktopData = () => {
    return (
      (desktop.activity.data !== null &&
        (desktop.activity.data.interactionStates.length > 0 || desktop.activity.data.appUse.length > 0)) ||
      desktop.activity.loading ||
      desktop.events.loading
    )
  }

  const shouldHideDesktop = !hasPermission(PERMISSIONS.VIEW_DESKTOP_ANALYTICS) || !hasDesktopData()

  const shouldHidePhraseHits =
    contactType === CONTACT_TYPE.SCREEN_ONLY ||
    !hasPermission(PERMISSIONS.VIEW_AUDIO_ANALYTICS) ||
    (!speech.loading && speech.data.hits && speech.data.hits.length === 0 && !hasTranscriptionHits)

  const shouldHideTranscription =
    contactType === CONTACT_TYPE.SCREEN_ONLY ||
    !hasPermission(PERMISSIONS.VIEW_LVCSR_ANALYTICS) ||
    (!transcription.loading && transcription.texts.length === 0)

  const shouldRenderMultiEval = useFeatureFlagValue(FEATURE_FLAGS.QM_MULTIEVAL_FORM)

  const shouldHideSentiment = shouldHideTranscription ||
    contact.data.sentiment === undefined ||
    contact.data.sentiment === null

  const shouldHideAdvSentiment = shouldHideTranscription || !advSentimentTrendGraphHasData
    || !licensedFeatures?.data.includes('EnterpriseFeatures') || !hasAnyPermission(PAGE_PERMISSIONS.SENTIMENT_VIEW)

  const shouldHideAudioPanel =
    contactType === CONTACT_TYPE.SCREEN_ONLY || ((hasEnergyData === false || hasAudioData === false) && !energy.error)

  const setTimeFromAudioOffset = time => onSetTime(time + audioStart)

  const renderNonCallPanels = () => {
    const shouldRenderEvalPanel = evaluationCanRender() || calibrationCanRender()
    return (
      <Fragment>
        <Grid
          item
          xs={!isExpanded || isEmpty(contact.data) ? 12 : isEvalDocked ? 4 : 6}
          className={classNames(classes.screenCapture, {
            [classes.halfHeight]: !isEmpty(contact.data) && isEvalDocked && !isExpanded,
          })}
        >
          {renderExplore()}
        </Grid>
        {shouldRenderEvalPanel && (
          <Grid
            item
            container
            xs={isExpanded ? 4 : 12}
            className={classNames({
              [classes.halfHeight]: !isExpanded,
              [classes.hidden]: !isEvalDocked,
            })}
          >
            {renderEvaluationForm()}
          </Grid>
        )}
      </Fragment>
    )
  }

  const renderWorkspaceNonCall = () => {
    if (isExpanded) {
      return renderNonCallPanels()
    } else {
      return (
        <Grid container direction={'column'} justifyContent="center">
          {renderNonCallPanels()}
        </Grid>
      )
    }
  }

  const renderTextPanels = () => {
    const showText = hasPermission(PERMISSIONS.VIEW_TEXT_ANALYTICS)
    const shouldRenderEvalPanel = evaluationCanRender() || calibrationCanRender()

    return (
      <Fragment>
        <Grid
          item
          xs={isExpanded ? 4 : 12}
          classes={isExpanded && { item: classes.exploreItem }}
          className={!isExpanded && (showText || isEvalDocked) && classes.hidden}
        >
          {renderExplore()}
        </Grid>
        <Grid
          item
          container
          xs={isExpanded ? 4 : 12}
          className={classNames({
            [classes.halfHeight]: !isExpanded,
          })}
        >
          {renderText()}
        </Grid>
        {shouldRenderEvalPanel && (
          <Grid
            item
            container
            xs={isExpanded ? 4 : 12}
            className={classNames({
              [classes.halfHeight]: !isExpanded,
              [classes.hidden]: !isEvalDocked,
            })}
          >
            {renderEvaluationForm()}
          </Grid>
        )}
      </Fragment>
    )
  }

  const renderChatPanels = () => {

    const shouldRenderEvalPanel = evaluationCanRender() || calibrationCanRender()

    return (
      <SelectedChatBubbleContext.Provider
        value={{
          selectedBubbleId: selectedBubble,
          setSelectedBubbleId: setSelectedBubble,
        }}
      >
        <Grid
          item
          xs={isEvalDocked ? 2 : 4}
          classes={isExpanded && { item: classes.exploreItem }}
          className={!isExpanded && isEvalDocked && classes.hidden}
        >
          {renderExplore()}
        </Grid>
        <Grid container item wrap="nowrap" classes={{ container: classes.chatContainer }}>
          <Grid item classes={{ item: classes.chatPanel }}>
            {renderChat()}
          </Grid>
          <Grid item className={!isExpanded ? classes.hidden : classes.chatMapPanel}>
            {renderChatMap()}
          </Grid>
        </Grid>
        {shouldRenderEvalPanel && (
          <Grid
            item
            container
            xs={isExpanded ? 3 : 12}
            className={classNames({
              [classes.halfHeight]: !isExpanded,
              [classes.hidden]: !isEvalDocked,
            })}
          >
            {renderEvaluationForm()}
          </Grid>
        )}
      </SelectedChatBubbleContext.Provider>
    )
  }

  const renderWorkspaceText = () => {
    if (isExpanded) {
      return renderTextPanels()
    } else {
      return (
        <Grid container direction={'column'} justifyContent="center">
          {renderTextPanels()}
        </Grid>
      )
    }
  }

  const renderWorkspaceChat = () => {
    if (isExpanded) {
      return renderChatPanels()
    } else {
      return (
        <Grid container direction={'column'} justifyContent="center">
          {renderChatPanels()}
        </Grid>
      )
    }
  }

  const renderScreenCapture = () => {
    const { onVideoCanPlay, onTimeUpdateFromVideo } = props

    const {
      data: { video },
      loading,
      error,
    } = storageFile

    const isVideoPlaying = isMediaPlaying && mediaCurrentTime >= videoStart && mediaCurrentTime < videoEnd

    const videoCurrentTime = Math.min(mediaCurrentTime - videoStart, videoEnd)

    return (
      <ErrorBoundary key={ccrid}>
        <ScreenCapture
          loading={loading}
          error={error}
          videoData={!videoRestoreDeclined && video}
          isPlaying={isVideoPlaying}
          playbackRate={mediaPlaybackRate}
          currentTime={videoCurrentTime}
          onCanPlay={onVideoCanPlay}
          onTimeUpdate={onTimeUpdateFromVideo}
          videoDeclined={videoRestoreDeclined}
          isDocked={isScreenDocked}
          setDocked={setIsScreenDocked}
          arePopupsEnabled={arePopupsEnabled}
          onPopupError={handlePopupError}
          onPopupsBlocked={onPopupsBlocked}
          onVideoHasErrors={onVideoHasErrors}
          classes={{ root: classes.flexGrow }}
          videoUploadFailed={videoUploadFailed}
        />
      </ErrorBoundary>
    )
  }

  renderScreenCapture.propTypes = {
    onVideoCanPlay: PropTypes.func.isRequired,
    onTimeUpdateFromVideo: PropTypes.func.isRequired,
  }

  const renderSpeech = () => {
    const { isRootRecording, evaluation } = contact.data
    const evalId = evaluation ? evaluation.id : null

    return (
      <ErrorBoundary key={ccrid}>
        <SpeechHitsPanel
          contactId={ccrid}
          callDurationMs={duration}
          audioDurationMs={audioDuration}
          audioOffsetMs={audioStart}
          storageFileOffsetMs={audioStorageFileOffset}
          isRootRecording={isRootRecording}
          evalId={evalId}
          currentTime={mediaCurrentTime}
          setTime={onSetTime}
          zoomed={isZoomed}
        />
      </ErrorBoundary>
    )
  }

  const renderChatMap = () => {
    const { evaluation } = contact.data
    const evalId = evaluation ? evaluation.id : null
    return (
      <ErrorBoundary key={ccrid}>
        <ChatMapPanel
          ccrId={ccrid}
          evalId={evalId}
          chat={chat}
          agentAcdId={contact.data.agent.agentAcdId}
          loading={chat?.text?.loading}
        />
      </ErrorBoundary>
    )
  }

  const renderText = () => {
    return (
      <ErrorBoundary key={ccrid}>
        <TextPanel
          ccrid={ccrid}
          contactType={contact.data.contactType}
          goalId={goalId}
        />
      </ErrorBoundary>
    )
  }

  const renderChat = () => {
    return (
      <ErrorBoundary key={ccrid}>
        <Chat
          agent={contact.data.agent}
          ccrid={ccrid}
          contactType={contact.data.contactType}
          loading={chat?.text?.loading}
          error={chat?.text?.error}
          goalId={goalId}
        />
      </ErrorBoundary>
    )
  }

  const renderAudio = (areEventsRightAligned = false) => {
    const { isRootRecording, evaluation } = contact.data
    const evalId = evaluation ? evaluation.id : null

    return (
      <ErrorBoundary key={ccrid}>
        <AudioPanel
          contactId={ccrid}
          callDurationMs={duration}
          audioDurationMs={audioDuration}
          audioOffsetMs={audioStart}
          storageFileOffsetMs={audioStorageFileOffset}
          isRootRecording={isRootRecording}
          evalId={evalId}
          currentTime={mediaCurrentTime}
          setTime={onSetTime}
          showSpeechHits={false}
          areEventsRightAligned={areEventsRightAligned}
          zoomed={isZoomed}
          isAudioDelayed={false}
        />
      </ErrorBoundary>
    )
  }

  const renderDesktop = () => {
    const { isRootRecording } = contact.data
    return (
      <ErrorBoundary key={ccrid}>
        <DesktopPanel
          ccrid={ccrid}
          startTime={contact.data.startTime}
          currentTime={mediaCurrentTime}
          callDuration={contact.data.callDuration}
          setTime={onSetTime}
          zoomed={isZoomed}
          isRootRecording={isRootRecording}
        />
      </ErrorBoundary>
    )
  }

  const renderAdvSentiment = () => {
    return (
      <ErrorBoundary key={ccrid}>
        <AdvSentiment
          contactId={ccrid}
          callDuration={contact.data.callDuration}
        />
      </ErrorBoundary>
    )
  }

  const renderSentiment = () => {
    return (
      <ErrorBoundary key={ccrid}>
        <Sentiment
          ccrid={ccrid}
          currentTime={mediaCurrentTime}
          callDuration={contact.data.callDuration}
          setTime={setTimeFromAudioOffset}
          zoomed={isZoomed}
          startTime={audioStart}
        />
      </ErrorBoundary>
    )
  }

  const renderTranscription = () => {
    const silenceEventDuration = 5000 // FIXME:

    return (
      <ErrorBoundary key={ccrid}>
        <Transcription
          ccrid={ccrid}
          isRootRecording={contact.data.isRootRecording}
          silenceEventDuration={silenceEventDuration}
          currentTime={mediaCurrentTime}
          audioOffset={audioStart}
          setTime={onSetTime}
        />
      </ErrorBoundary>
    )
  }

  const renderSummary = () => {
    return (
      <ErrorBoundary key={ccrid}>
        <InteractionSummary ccrId={ccrid} clientAppId={'C1MediaPlayerUI'}
          autoQAHasData={autoQAAIAnalysisHasData}
          autoQAReloadTimestamp={autoQAReloadTimestamp}
          autoQAFormId={autoQAFormId}
        />
      </ErrorBoundary>
    )
  }

  const renderExplore = () => {
    const { data, loading = false, error = false } = contact

    return (
      <ErrorBoundary key={ccrid}>
        <Explore
          contact={data}
          timezoneOverride={timezoneOverride}
          loading={loading}
          error={error}
          openContact={onOpenContact}
          hasDx={hasDx}
        />
      </ErrorBoundary>
    )
  }

  const evaluationCanRender = () => {
    if (isEmpty(contact.data)) {
      return false
    }

    const {
      data: {
        evalStateId,
        agent: { agentId },
        evaluator,
      },
    } = contact

    const evaluatorId = evaluator['$ref']
      ? parseInt(evaluator['$ref'].substring(evaluator['$ref'].lastIndexOf('/') + 1))
      : -1

    return canEvaluationComponentRender({ hasPermission, hasAllPermissions, user }, { evalStateId, agentId, evaluatorId })
  }

  const calibrationCanRender = () => {
    if (isEmpty(contact.data)) {
      return false
    }

    const {
      data: { isCalibration, agent: { agentId } },
    } = contact

    return canCalibrationComponentRender({ hasPermission, user }, agentId, isCalibration)
  }

  const renderEvaluationForm = () => {
    if (isEmpty(contact.data)) {
      return null
    }

    const getContactDateTimeString = () => {
      const tz = timezoneOverride ? timezoneOverride : contact.data.tz
      const contactDate = moment(contact.data.startTime).tz(tz).format('L')
      const contactTime = moment(contact.data.startTime).tz(tz).format('LT')
      return `${contactDate} ${contactTime}`
    }

    const chatObject = chat?.text?.data?.chatBody ? chat.text.data.chatBody : null
    const messages = chatObject ? chatObject.messages : []
    const earliestMessage = messages && messages.length > 0 ? messages.reduce((m1, m2) => m1.sendTimeMs < m2.sendTimeMs ? m1 : m2, messages[0]) : null
    const participants = chatObject ? chatObject.participants : null

    const shouldShowManualEvalForm = evaluationCanRender() || calibrationCanRender()
    const shouldShowAiEvalForm = hasAutoQAPermission && (
      (!transcription.loading && transcription.texts.length > 0) ||
      (contactType === CONTACT_TYPE.EMAIL && !chat?.text?.loading && messages && messages.length > 0) ||
      (contactType === CONTACT_TYPE.CHAT && !chat?.text?.loading && messages && messages.length > 0)
    )

    // If this contact is a chat or sms, index the bubbles for comment display
    const indexedChatBubbles = (earliestMessage && messages && participants) ?
      chat?.text?.data?.chatBody.messages.map((message, index) => {
        const participant = participants.find(participant => participant.id === message.authorId)
        // Author label follows the folloing hierarchy, depedning on the data available:
        // agent display name, message label attribute, author external ID, participant numeric index.
        const authorName = agentData.find(a => a.acdId === participant?.externalId)?.name
          || participant?.label
          || participant?.externalId
          || (participant?.id && `Participant ${participant?.id}`)
          || 'Unknown Message Author'
        return {
          index,
          authorName,
          relativeTimestampMs: message.sendTimeMs - earliestMessage.sendTimeMs,
        }
      })
      : []

    return (shouldRenderMultiEval
      ? (
        <ErrorBoundary key={ccrid}>
          <MultiEvaluation />
        </ErrorBoundary>
      )
      : (
        <ErrorBoundary key={ccrid}>
          <SingleEvaluation
            contactId={contact.data.id}
            evalId={contact.data.evaluation !== null ? contact.data.evaluation.id : null}
            evalFormId={contact.data.evalForm !== null ? contact.data.evalForm.evalFormId : null}
            goal={contact.data.goal}
            agentId={contact.data.agent.agentId}
            agentDisplayName={contact.data.agent.displayId}
            contactDateTime={getContactDateTimeString()}
            callDuration={contact.data.callDuration}
            qualityEvaluationAllowed={evaluationCanRender()}
            calibrationEvaluationAllowed={calibrationCanRender()}
            isDocked={isEvalDocked}
            setDocked={setIsEvalDocked}
            arePopupsEnabled={arePopupsEnabled}
            onPopupError={handlePopupError}
            onPopupsBlocked={onPopupsBlocked}
            onGetMediaCurrentTime={onGetMediaCurrentTime}
            showManualEvalForm={shouldShowManualEvalForm}
            showAiEvalForm={shouldShowAiEvalForm}
            indexedChatBubbles={indexedChatBubbles}
            autoQAHasData={autoQAAutoEvaluationHasData}
            autoQAFormId={autoQAFormId}
          />
        </ErrorBoundary>
      ))
  }

  const renderVideoEvalColumn = ({ gridColumns, style = {} }) => {
    /**
     * Render means React render, but the component may be hidden
     * Show means React render and the component is visible
     * We render, but not show, the Screen Capture or Eval panels when they are popped out
     * because they aren't to be shown in the Grid, but rather in their popup windows.
     */

    const shouldRenderScreenCapture = videoUploadFailed || (hasVideo && hasVideoData !== false)
    const shouldShowScreenCapture = shouldRenderScreenCapture && isScreenDocked
    const shouldShowAiEvalForm = !shouldHideTranscription && hasAutoQAPermission
    const shouldRenderEvalPanel = evaluationCanRender() || calibrationCanRender() || shouldShowAiEvalForm
    const shouldShowEvalPanel = shouldRenderEvalPanel && isEvalDocked
    const shouldRenderExplore = !shouldShowScreenCapture && !shouldShowEvalPanel && !isExpanded

    return (
      <Grid
        item
        container
        direction="column"
        spacing={0}
        wrap="nowrap"
        className={classNames(
          !shouldShowScreenCapture && !shouldShowEvalPanel && !shouldRenderExplore && classes.hidden,
        )}
        xs={gridColumns}
        style={style}
      >
        {shouldRenderScreenCapture && (
          <Grid
            item
            className={classNames(classes.screenCapture, {
              [classes.halfHeight]: shouldShowEvalPanel,
              [classes.hidden]: !isScreenDocked,
            })}
          >
            {renderScreenCapture()}
          </Grid>
        )}

        {shouldRenderEvalPanel && (
          <Grid
            item
            container
            xs={12}
            className={classNames(classes.halfHeight, {
              [classes.hidden]: !isEvalDocked,
            })}
          >
            {renderEvaluationForm()}
          </Grid>
        )}

        {shouldRenderExplore && <Grid item> {renderExplore()} </Grid>}
      </Grid>
    )
  }

  renderVideoEvalColumn.propTypes = {
    gridColumns: PropTypes.number,
    style: PropTypes.object,
  }

  const renderTimeline = timeMarkerWidth => {
    return (
      <ErrorBoundary key={ccrid}>
        <TimelinePanel
          contactId={ccrid}
          duration={contact.data.callDuration}
          currentTime={mediaCurrentTime}
          timeMarkerWidth={timeMarkerWidth}
          isZoomed={isZoomed}
          onScrubStart={onScrubStart}
          onScrubEnd={onScrubEnd}
        />
      </ErrorBoundary>
    )
  }

  const renderWorkspaceScreenOnly = () => {
    const scrubberWidth = theme.spacing(PANEL_SIZES.TIMELINE)
    const desktopPanelWidth = theme.spacing(PANEL_SIZES.DESKTOP)
    const analyticsWidth = scrubberWidth + (!shouldHideDesktop && isExpanded ? desktopPanelWidth : 0)

    return (
      <Fragment>
        <Grid
          item
          xs={(hasVideo && isScreenDocked) || isEvalDocked ? 2 : 4}
          style={colWidth(`calc(50% - ${analyticsWidth}px)`)}
          classes={{ item: classes.exploreItem }}
          className={!isExpanded && classes.hidden}
        >
          {renderExplore()}
        </Grid>
        <Grid
          item
          container
          xs={null}
          style={{
            ...colWidth(`${analyticsWidth}px`),
            ...(!isExpanded ? { position: 'relative', left: theme.spacing(1) } : {}),
          }}
        >
          {renderAnalyticsColumns()}
        </Grid>
        {renderVideoEvalColumn({
          gridColumns: isExpanded ? 6 : 9,
          style: isExpanded ? {} : colWidth(`calc(100% - ${analyticsWidth}px)`),
        })}
      </Fragment>
    )
  }

  const renderRootCallPanels = () => {
    return (
      <Fragment>
        <Grid container item wrap="nowrap" xs={isExpanded ? 5 : 12}>
          <Grid container wrap="nowrap" classes={{ container: classes.timelineAudioContainer }}>
            <Grid item classes={{ item: classes.timelinePanel }}>
              {/* the timeMarker will be as wide as the audio panel */}
              {renderTimeline(theme.spacing(PANEL_SIZES.AUDIO))}
            </Grid>
            <Grid item>{renderAudio(true)}</Grid>
          </Grid>
          <Grid item className={classes.flexGrow}>
            {renderExplore()}
          </Grid>
        </Grid>
      </Fragment>
    )
  }

  const renderAnalyticsColumns = () => {
    const desktopPanelWidth = theme.spacing(PANEL_SIZES.DESKTOP)
    const sentimentPanelWidth = theme.spacing(PANEL_SIZES.SENTIMENT)
    const speechPanelWidth = theme.spacing(PANEL_SIZES.SPEECH)
    const audioPanelWidth = theme.spacing(PANEL_SIZES.AUDIO)

    const hideTimeMarker =
      !shouldHideAudioPanel && energy && energy.loading ||
      !shouldHideTranscription && transcription && transcription.loading ||
      !shouldHidePhraseHits && speech && speech.loading ||
      !shouldHideDesktop && (
        desktop.activity && desktop.activity.loading ||
        desktop.energy && desktop.energy.loading
      )

    const timeMarkerWidth = hideTimeMarker ? 0 : (
      (shouldHideAudioPanel ? 0 : audioPanelWidth) +
      (shouldHideDesktop || !isExpanded ? 0 : desktopPanelWidth) +
      ((advSentimentEnabled && shouldHideAdvSentiment) || (!advSentimentEnabled && shouldHideSentiment) || !isExpanded ? 0 : sentimentPanelWidth) +
      (shouldHidePhraseHits || !isExpanded ? 0 : speechPanelWidth)
    )

    return (
      <Fragment>
        {shouldHideTranscription ? null : renderSummary()}
        <Grid container item wrap="nowrap" classes={{ container: classes.analyticsContainer }}>
          {shouldHideTranscription ? null : (
            <Grid item classes={{ item: classes.transcriptionPanel }} className={!isExpanded ? classes.hidden : ''}>
              {renderTranscription()}
            </Grid>
          )}
          <Grid item classes={{ item: classes.timelinePanel }}>
            {renderTimeline(timeMarkerWidth)}
          </Grid>
          {shouldHideAudioPanel ? null : (
            <Grid item classes={{ item: classes.audioPanel }}>
              {renderAudio(true)}
            </Grid>
          )}
          {!advSentimentEnabled || shouldHideAdvSentiment ? null : (
            <Grid item classes={{ item: classes.sentimentPanel }} className={!isExpanded ? classes.hidden : ''}>
              {renderAdvSentiment()}
            </Grid>
          )}
          {advSentimentEnabled || shouldHideSentiment ? null : (
            <Grid item classes={{ item: classes.sentimentPanel }} className={!isExpanded ? classes.hidden : ''}>
              {renderSentiment()}
            </Grid>
          )}
          {shouldHidePhraseHits ? null : (
            <Grid item className={!isExpanded ? classes.hidden : ''}>
              {renderSpeech(true)}
            </Grid>
          )}
          {shouldHideDesktop ? null : (
            <Grid item classes={{ item: classes.desktopPanel }} className={!isExpanded ? classes.hidden : ''}>
              {renderDesktop()}
            </Grid>
          )}
        </Grid>
      </Fragment>
    )
  }

  const renderWorkspaceNoTranscription = () => {
    const scrubberWidth = theme.spacing(PANEL_SIZES.TIMELINE)
    const desktopPanelWidth = theme.spacing(PANEL_SIZES.DESKTOP)
    const speechPanelWidth = theme.spacing(PANEL_SIZES.SPEECH)
    const audioPanelWidth = theme.spacing(PANEL_SIZES.AUDIO)

    const analyticsWidth =
      scrubberWidth +
      (shouldHideAudioPanel ? 0 : audioPanelWidth) +
      (shouldHideDesktop ? 0 : desktopPanelWidth) +
      (shouldHidePhraseHits ? 0 : speechPanelWidth)

    return (
      <Fragment>
        <Grid
          item
          style={colWidth(`calc(50% - ${analyticsWidth}px)`)}
          classes={{ item: classes.exploreItem }}
          className={!isExpanded ? classes.hidden : ''}
        >
          {renderExplore()}
        </Grid>
        <Grid
          item
          container
          xs={isExpanded ? null : shouldHideAudioPanel ? 1 : 3}
          style={isExpanded ? colWidth(`${analyticsWidth}px`) : null}
        >
          {renderAnalyticsColumns()}
        </Grid>
        {renderVideoEvalColumn({ gridColumns: isExpanded ? 6 : shouldHideAudioPanel ? 11 : 9, style: {} })}
      </Fragment>
    )
  }

  const renderWorkspaceWithTranscription = () => {
    return (
      <Fragment>
        <Grid
          item
          xs={(hasVideo && isScreenDocked) || isEvalDocked ? 2 : 4}
          classes={{ item: classes.exploreItem }}
          className={!isExpanded ? classes.hidden : ''}
        >
          {renderExplore()}
        </Grid>
        <Grid item container xs={isExpanded ? 7 : 3} className={classes.transcriptionParent}>
          {renderAnalyticsColumns()}
        </Grid>
        {renderVideoEvalColumn({ gridColumns: isExpanded ? 3 : 9, style: {} })}
      </Fragment>
    )
  }

  const renderWorkspace = () => {
    const textOnly = contactType === CONTACT_TYPE.TEXT || (contactType === CONTACT_TYPE.EMAIL && recordingType === 'NONE')
    if (textOnly) {
      return renderWorkspaceText()
    }

    const chatOnly = contactType === CONTACT_TYPE.CHAT || contactType === CONTACT_TYPE.SMS || contactType === CONTACT_TYPE.EMAIL
    if (chatOnly) {
      return renderWorkspaceChat()
    }

    // if the contact hasn't loaded, show just the Explore and Evaluation (if permitted)
    if (isEmpty(contact.data) || contactType === CONTACT_TYPE.NON_CALL) {
      return renderWorkspaceNonCall()
    }

    if (contactType === CONTACT_TYPE.SCREEN_ONLY) {
      return renderWorkspaceScreenOnly()
    }

    if (isRootRecording) {
      return renderRootCallPanels()
    }

    return shouldHideTranscription ? renderWorkspaceNoTranscription() : renderWorkspaceWithTranscription()
  }

  return renderWorkspace()
}

MediaPlayerWorkspace.propTypes = {
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  ccrid: PropTypes.number,
  media: PropTypes.shape({
    main: PropTypes.object.isRequired,
    /** speech hits */
    speech: dataWrapperPropType,
    /** events */
    events: dataWrapperPropType,
    /** energy */
    energy: dataWrapperPropType,
    transcription: PropTypes.object,
    desktop: PropTypes.object,
    chat: PropTypes.object,
  }),
  authenticatedUser: authenticatedUserShape.isRequired,
  mediaTimeline: PropTypes.object.isRequired,
  timezoneOverride: PropTypes.string,
  role: PropTypes.string,
  isExpanded: PropTypes.bool.isRequired,
  mediaCurrentTime: PropTypes.number,
  mediaPlaybackRate: PropTypes.number,
  isZoomed: PropTypes.bool,
  isMediaReady: PropTypes.bool,
  areCalibrationsEnabled: PropTypes.bool,
  hasVideo: PropTypes.bool,
  hasAudioData: PropTypes.bool,
  hasVideoData: PropTypes.bool,
  hasDx: PropTypes.bool,
  onSetTime: PropTypes.func.isRequired,
  videoRestoreDeclined: PropTypes.bool,
  onOpenContact: PropTypes.func.isRequired,
  onScrubStart: PropTypes.func.isRequired,
  onScrubEnd: PropTypes.func.isRequired,
  onGetMediaCurrentTime: PropTypes.func.isRequired,
  onVideoCanPlay: PropTypes.func.isRequired,
  onTimeUpdateFromVideo: PropTypes.func.isRequired,
  onPopupsBlocked: PropTypes.func.isRequired,
  onVideoHasErrors: PropTypes.func,
  videoUploadFailed: PropTypes.func,
  goalId: PropTypes.number,
  autoQAAIAnalysisHasData: PropTypes.bool,
  autoQAAutoEvaluationHasData: PropTypes.bool,
  autoQAReloadTimestamp: PropTypes.number,
  autoQAFormId: PropTypes.number,
}

export default withStyles(styles, { withTheme: true })(withTranslation()(withAuthenticatedUser(MediaPlayerWorkspace)))
